import { useI18n } from 'vue-i18n'
import { isImageMime, isInlineViewable } from 'models'
import { useImageFallback } from './useImageFallback'

/** Minimal shape a file-list row needs for the shared display helpers. */
export interface FileRowLike {
    documentId: string
    mimeType: string | null
}

/**
 * Shared display logic for the user-files list rows, used by both the in-app
 * table (PjUserFilesTable) and the embedded widget (PkChatbotViewFiles). The two
 * surfaces differ only in how they build URLs (authenticated store vs widget API
 * client), so the URL builders are injected.
 *
 * - `view(id, inline)` → opens/downloads the original file.
 * - `preview(id)`      → small resized blob for the row thumbnail.
 */
export function useFileRow(urls: {
    view: (documentId: string, inline: boolean) => string
    preview: (documentId: string) => string
}) {
    const { locale } = useI18n({ useScope: 'global' })

    // Image rows show a thumbnail; if it fails to load (auth/redirect, or the
    // preview isn't generated yet) we fall back to the file-type icon.
    const { canShowImage, onImageError } = useImageFallback()
    const thumbUrl = (file: FileRowLike): string =>
        urls.preview(file.documentId)
    const showThumb = (file: FileRowLike): boolean =>
        isImageMime(file.mimeType) && canShowImage(thumbUrl(file))
    const onThumbError = (file: FileRowLike): void => {
        onImageError(thumbUrl(file))
    }

    // Name click previews images/PDF inline; an explicit download forces an
    // attachment (inline=false → download SAS).
    const openFile = (file: FileRowLike, download = false): void => {
        const inline = !download && isInlineViewable(file.mimeType)
        window.open(urls.view(file.documentId, inline), '_blank', 'noopener')
    }

    const formatDate = (iso: string): string =>
        new Date(iso).toLocaleDateString(locale.value, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })

    return { showThumb, thumbUrl, onThumbError, openFile, formatDate }
}
